import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { LocationInput } from "@/components/LocationInput";
import { MapPicker } from "@/components/MapPicker";
import { ImageUpload } from "@/components/ImageUpload";
import { CAPACITY_OPTIONS, TRAVEL_MODES, VEHICLE_MODES, VEHICLE_TYPES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plane, Car } from "lucide-react";

const empty = {
  origin: "", destination: "", travel_date: "", travel_time: "", capacity: "", notes: "",
  origin_lat: null, origin_lng: null, dest_lat: null, dest_lng: null,
  travel_mode: "", vehicle_type: "", vehicle_model: "", vehicle_reg: "", vehicle_photo_id: null,
};

export default function CreateTrip() {
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const valid = form.origin && form.destination && form.travel_date && form.capacity && form.travel_mode;
  const needsVehicle = VEHICLE_MODES.includes(form.travel_mode);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { toast.error("Please complete all required fields"); return; }
    if (form.origin.trim().toLowerCase() === form.destination.trim().toLowerCase()) { toast.error("Starting location and destination cannot be the same"); return; }
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (new Date(form.travel_date) < today) { toast.error("Travel date cannot be in the past"); return; }
    if (needsVehicle && form.vehicle_reg && !/^[A-Za-z0-9 -]{4,15}$/.test(form.vehicle_reg.trim())) { toast.error("Please enter a valid registration number"); return; }
    setLoading(true);
    try {
      await api.post("/trips", form);
      toast.success("Your trip has been published.");
      navigate("/my-trips");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title="Post a Trip" subtitle="Publish your route so senders can request deliveries along your way." />
      <form onSubmit={submit} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm" data-testid="create-trip-form">
        <div className="grid sm:grid-cols-2 gap-5">
          <LocationInput label="Starting Location" required testid="trip-origin-input" value={form.origin}
            onChange={({ text, lat, lng }) => setForm((f) => ({ ...f, origin: text, origin_lat: lat ?? null, origin_lng: lng ?? null }))} />
          <LocationInput label="Destination" required testid="trip-destination-input" value={form.destination}
            onChange={({ text, lat, lng }) => setForm((f) => ({ ...f, destination: text, dest_lat: lat ?? null, dest_lng: lng ?? null }))} />
        </div>

        <MapPicker
          origin={form.origin_lat != null ? { lat: form.origin_lat, lng: form.origin_lng } : null}
          destination={form.dest_lat != null ? { lat: form.dest_lat, lng: form.dest_lng } : null}
          originLabel="Start" destinationLabel="Destination" height={260}
          onPick={(which, p) => setForm((f) => which === "origin"
            ? { ...f, origin: p.address, origin_lat: p.lat, origin_lng: p.lng }
            : { ...f, destination: p.address, dest_lat: p.lat, dest_lng: p.lng })}
        />

        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <Label>Travel Date <span className="text-rose-500">*</span></Label>
            <Input type="date" data-testid="trip-date-input" value={form.travel_date} onChange={(e) => set("travel_date", e.target.value)} className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label>Travel Time</Label>
            <Input type="time" data-testid="trip-time-input" value={form.travel_time} onChange={(e) => set("travel_time", e.target.value)} className="rounded-xl" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <Label>Travel Mode <span className="text-rose-500">*</span></Label>
            <Select value={form.travel_mode} onValueChange={(v) => set("travel_mode", v)}>
              <SelectTrigger data-testid="trip-mode-select" className="rounded-xl"><SelectValue placeholder="How are you travelling?" /></SelectTrigger>
              <SelectContent>{TRAVEL_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Available Package Capacity <span className="text-rose-500">*</span></Label>
            <Select value={form.capacity} onValueChange={(v) => set("capacity", v)}>
              <SelectTrigger data-testid="trip-capacity-select" className="rounded-xl"><SelectValue placeholder="Select capacity" /></SelectTrigger>
              <SelectContent>{CAPACITY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {needsVehicle && (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-4" data-testid="vehicle-section">
            <p className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Car className="w-4 h-4 text-primary" /> Vehicle details</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Vehicle Type</Label>
                <Select value={form.vehicle_type} onValueChange={(v) => set("vehicle_type", v)}>
                  <SelectTrigger data-testid="vehicle-type-select" className="rounded-xl bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{VEHICLE_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Vehicle Model</Label>
                <Input data-testid="vehicle-model-input" value={form.vehicle_model} onChange={(e) => set("vehicle_model", e.target.value)} placeholder="e.g. Royal Enfield GT 650" className="rounded-xl bg-white" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Registration Number</Label>
              <Input data-testid="vehicle-reg-input" value={form.vehicle_reg} onChange={(e) => set("vehicle_reg", e.target.value)} placeholder="e.g. KA 01 AB 1234" className="rounded-xl bg-white" />
            </div>
            <div className="space-y-1.5">
              <Label>Vehicle Photo <span className="text-slate-400 font-normal">(optional)</span></Label>
              <ImageUpload value={form.vehicle_photo_id} onChange={(id) => set("vehicle_photo_id", id)} purpose="vehicle" testid="vehicle-photo" hint="Shown only to senders you have an accepted connection with." />
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Additional Information</Label>
          <Textarea data-testid="trip-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Route stops, restrictions, preferences…" className="rounded-xl" rows={3} />
        </div>

        <Button type="submit" disabled={loading} data-testid="publish-trip-button" className="w-full rounded-xl h-11">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plane className="w-4 h-4" /> Publish Trip</>}
        </Button>
      </form>
    </div>
  );
}
